// FieldVision - MineMarkerOversigt.tsx
// Mine Marker oversigt side med split-view layout: sidebar + Leaflet kort
// FIX: Bruger CVR-Marknr som unik key for deduplicering (matcher App.tsx)
// FIX: Bruger batch endpoint for kort-taelling (performance)
// NEW: Papirkurv-ikon i header med badge for slettede kort
// FIX: batch-counts nu med CVR parameter for korrekt taelling per CVR (2025-12-30)
// FIX: Danske karakterer rettet (2025-12-31)
// FIX 2025-12-31: handleFieldClick bruger nu CVR-Marknr for korrekt field lookup

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MarkerListe from './MarkerListe';
import MarkerMap from './MarkerMap';
import TrashModal from './TrashModal';
import apiService from '../services/api';

interface Field {
  Id: number;
  Marknr: string;
  CVR: string;
  IMK_areal: number;
  Geometry?: string;
  CenterLat?: number;
  CenterLon?: number;
  isZoomLoaded?: boolean;
}

interface MarkWithMaps extends Field {
  maps: {
    hasNDVI: boolean;
    hasUkrudt: boolean;
    totalMaps: number;
  };
}

interface NavigationFieldData {
  marknr: string;
  cvr: string;
  areal: number;
  id: number;
  geometry?: string;
}

interface MineMarkerOversigtProps {
  onBack: () => void;
  onNavigateToField: (marknr: string, fieldData?: NavigationFieldData) => void;
  onNavigateToAddFields: () => void;
  userCVRs: string[];
  zoomLoadedFields?: any[];
  selectedFieldIds?: string[];
}

// Helper: Hent field ID fra feature objekt
const getFieldIdFromFeature = (feature: any): string => {
  if (!feature) return '';
  if ((feature as any).properties) {
    return (feature as any).properties.Marknr || (feature as any).properties.marknr ||
           (feature as any).properties.Id?.toString() || (feature as any).id?.toString() || '';
  }
  return (feature as any).Marknr || (feature as any).marknr || (feature as any).Id?.toString() || '';
};

// Helper til at faa det faktiske Marknr der vil blive gemt
const getActualMarknr = (feature: any): string => {
  const props = (feature as any).properties || feature;
  const fallbackId = getFieldIdFromFeature(feature);
  return (props as any).Marknr || (props as any).marknr || fallbackId;
};

// Helper: Hent CVR fra feature
const getActualCVR = (feature: any): string => {
  const props = (feature as any).properties || feature;
  return (props as any).CVR || (props as any).cvr || 'EKSTRA';
};

// Helper: Generer unik key (CVR-Marknr) - matcher App.tsx getFieldId()
const getUniqueFieldKey = (field: any): string => {
  const cvr = (field as any).CVR || (field as any).cvr || 'EKSTRA';
  const marknr = (field as any).Marknr || (field as any).marknr || '';
  return `${cvr}-${marknr}`;
};

// Helper: Naturlig/numerisk sortering af Marknr
// Haandterer formater som: "12-1a", "12-1b", "120-0", "15-0"
const parseMarknr = (marknr: string): { main: number; sub: number; suffix: string } => {
  if (!marknr) return { main: 0, sub: 0, suffix: '' };

  // Match pattern: tal-tal+bogstaver (fx "12-1a", "120-0", "15-0")
  const match = marknr.match(/^(\d+)-(\d+)([a-zA-Z]*)$/);

  if (match) {
    return {
      main: parseInt(match[1], 10),
      sub: parseInt(match[2], 10),
      suffix: match[3].toLowerCase()
    };
  }

  // Fallback: proev at parse som enkelt tal
  const numMatch = marknr.match(/^(\d+)(.*)$/);
  if (numMatch) {
    return {
      main: parseInt(numMatch[1], 10),
      sub: 0,
      suffix: numMatch[2].toLowerCase()
    };
  }

  // Sidste fallback: returner 0 og brug hele string som suffix
  return { main: 0, sub: 0, suffix: marknr.toLowerCase() };
};

const compareMarknr = (a: string, b: string): number => {
  const parsedA = parseMarknr(a);
  const parsedB = parseMarknr(b);

  // 1. Sorter efter hovedtal (12 < 15 < 120)
  if (parsedA.main !== parsedB.main) {
    return parsedA.main - parsedB.main;
  }

  // 2. Sorter efter undertal (0 < 1 < 2)
  if (parsedA.sub !== parsedB.sub) {
    return parsedA.sub - parsedB.sub;
  }

  // 3. Sorter efter bogstav-suffix (a < b < c)
  return parsedA.suffix.localeCompare(parsedB.suffix, 'da');
};

const MineMarkerOversigt: React.FC<MineMarkerOversigtProps> = ({
  onBack,
  onNavigateToField,
  onNavigateToAddFields,
  userCVRs,
  zoomLoadedFields = [],
  selectedFieldIds = []
}) => {
  const [fields, setFields] = useState<MarkWithMaps[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMarknr, setSelectedMarknr] = useState<string | null>(null);
  const [loadingMaps, setLoadingMaps] = useState(false);

  // Papirkurv state
  const [trashModalOpen, setTrashModalOpen] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);

  useEffect(() => {
    loadFields();
    loadDeletedCount();
  }, [userCVRs, zoomLoadedFields, selectedFieldIds]);

  // Hent antal slettede kort
  const loadDeletedCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/zonemaps/deleted/count`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDeletedCount(data.count || 0);
      }
    } catch (err) {
      console.warn('Could not load deleted count:', err);
    }
  };

  const loadFields = async () => {
    try {
      setLoading(true);
      setError(null);

      const allFields: Field[] = [];

      // 1. Hent CVR-baserede marker
      if (userCVRs && userCVRs.length > 0) {
        for (const cvr of userCVRs) {
          try {
            const cvrFields = await apiService.searchFields([cvr]);

            if ((cvrFields as any)?.features) {
              const mappedFields = (cvrFields as any).features.map((f: any) => ({
                Id: (f as any).properties.Id || (f as any).id,
                Marknr: (f as any).properties.Marknr,
                CVR: (f as any).properties.CVR || cvr,
                IMK_areal: (f as any).properties.IMK_areal || (f as any).properties.GEOMETRISK || 0,
                Geometry: JSON.stringify((f as any).geometry),
                CenterLat: (f as any).properties.CenterLat,
                CenterLon: (f as any).properties.CenterLon,
                isZoomLoaded: false
              }));

              allFields.push(...mappedFields);
            }
          } catch (err) {
            console.warn(`Could not load fields for CVR ${cvr}:`, err);
          }
        }
      }

      // 2. Tilføj zoom-loaded marker
      if (zoomLoadedFields && zoomLoadedFields.length > 0) {
        for (let i = 0; i < (zoomLoadedFields as any[]).length; i++) {
          const zoomField = zoomLoadedFields[i];
          const actualMarknr = getActualMarknr(zoomField);
          const actualCVR = getActualCVR(zoomField);

          // FIX: Brug CVR-Marknr som unik key (matcher App.tsx)
          const uniqueKey = `${actualCVR}-${actualMarknr}`;

          // Check om CVR-Marknr kombinationen allerede findes i allFields
          const alreadyExists = allFields.some((f: any) =>
            getUniqueFieldKey(f) === uniqueKey
          );

          if (alreadyExists) {
            continue;
          }

          const props = (zoomField as any).properties || zoomField;
          const mappedField: Field = {
            Id: (props as any).Id || (props as any).id || Math.floor(Math.random() * 100000),
            Marknr: actualMarknr,
            CVR: actualCVR,
            IMK_areal: (props as any).IMK_areal || (props as any).GEOMETRISK || (props as any).areal || 0,
            Geometry: (zoomField as any).geometry ? JSON.stringify((zoomField as any).geometry) : (props as any).Geometry,
            CenterLat: (props as any).CenterLat,
            CenterLon: (props as any).CenterLon,
            isZoomLoaded: true
          };

          allFields.push(mappedField);
        }
      }

      // FIX: Dedupliker baseret paa CVR-Marknr (ikke kun Marknr)
      const uniqueFields = Array.from(
        new Map(allFields.map((f: any) => [getUniqueFieldKey(f), f])).values()
      );

      // Initialiser med tomme kort-info
      const fieldsWithEmptyMaps: MarkWithMaps[] = uniqueFields.map((f: any) => ({
        ...f,
        maps: { hasNDVI: false, hasUkrudt: false, totalMaps: 0 }
      }));

      // Filter fields based on selectedFieldIds (if provided)
      const filteredFields = selectedFieldIds.length > 0
        ? fieldsWithEmptyMaps.filter(f => selectedFieldIds.includes(`${f.CVR}-${f.Marknr}`))
        : fieldsWithEmptyMaps;
      setFields(filteredFields);
      setLoading(false);

      // Hent kort-antal for hver mark (via batch endpoint)
      loadMapsInfo(filteredFields as Field[]);
    } catch (err) {
      console.error('Error loading fields:', err);
      setError('Kunne ikke hente marker');
      setLoading(false);
    }
  };

  // FIX 2025-12-30: Hent kort-info via batch endpoint, GRUPPERET per CVR
  // Dette sikrer at kort-antal kun taeller kort fra det specifikke CVR
  const loadMapsInfo = async (fieldsToLoad: Field[]) => {
    if (fieldsToLoad.length === 0) {
      return;
    }

    setLoadingMaps(true);

    try {
      // FIX: Grupper marker efter CVR
      const fieldsByCvr: Record<string, Field[]> = {};
      fieldsToLoad.forEach((field: any) => {
        const cvr = (field as any).CVR || 'EKSTRA';
        if (!fieldsByCvr[cvr]) {
          fieldsByCvr[cvr] = [];
        }
        fieldsByCvr[cvr].push(field);
      });

      console.log('loadMapsInfo: Grouped fields by CVR:', Object.keys(fieldsByCvr));

      // FIX: Kald batch-counts for hver CVR-gruppe med CVR parameter
      const allCountsMap: Record<string, { totalMaps: number; hasNDVI: boolean; hasUkrudt: boolean }> = {};

      for (const cvr of Object.keys(fieldsByCvr)) {
        const cvrFields = fieldsByCvr[cvr];
        const marknrs = cvrFields
          .map((f: any) => (f as any).Marknr)
          .filter((m: any) => m);

        if (marknrs.length === 0) {
          continue;
        }

        console.log('loadMapsInfo: Fetching counts for CVR', cvr, 'with', marknrs.length, 'fields');

        try {
          // FIX: Send CVR med til batch-counts
          const countsMap = await (apiService as any).getZoneMapsBatchCounts(marknrs, cvr);

          // Map resultat til CVR-Marknr key saa vi kan matche korrekt
          cvrFields.forEach((field: any) => {
            const marknr = (field as any).Marknr;
            const uniqueKey = getUniqueFieldKey(field);
            const counts = countsMap[marknr] || { totalMaps: 0, hasNDVI: false, hasUkrudt: false };
            allCountsMap[uniqueKey] = counts;
          });
        } catch (err) {
          console.warn('loadMapsInfo: Error fetching counts for CVR', cvr, err);
          // Initialiser med 0 ved fejl
          cvrFields.forEach((field: any) => {
            const uniqueKey = getUniqueFieldKey(field);
            allCountsMap[uniqueKey] = { totalMaps: 0, hasNDVI: false, hasUkrudt: false };
          });
        }
      }

      console.log('loadMapsInfo: Total counts received for', Object.keys(allCountsMap).length, 'fields');

      // Map resultat til fields via CVR-Marknr key
      const updatedFields: MarkWithMaps[] = fieldsToLoad.map((field: any) => {
        const uniqueKey = getUniqueFieldKey(field);
        const counts = allCountsMap[uniqueKey] || { totalMaps: 0, hasNDVI: false, hasUkrudt: false };

        return {
          ...field,
          maps: {
            hasNDVI: counts.hasNDVI || false,
            hasUkrudt: counts.hasUkrudt || false,
            totalMaps: counts.totalMaps || 0
          }
        };
      });

      setFields(updatedFields);

    } catch (err) {
      // FIX: Log fejl i stedet for silent failure
      console.error('loadMapsInfo: Error fetching map counts:', err);

      // Fallback: proev individuelle kald hvis batch fejler
      console.log('loadMapsInfo: Falling back to individual requests...');
      await loadMapsInfoFallback(fieldsToLoad);
    }

    setLoadingMaps(false);
  };

  // Fallback: Individuelle API kald (bruges hvis batch endpoint fejler)
  const loadMapsInfoFallback = async (fieldsToLoad: Field[]) => {
    const updatedFields: MarkWithMaps[] = [];

    for (const field of fieldsToLoad) {
      try {
        const marknr = (field as any).Marknr;
        const cvr = (field as any).CVR;
        // FIX: Send CVR med til getZoneMapsByField
        const maps = await apiService.getZoneMapsByField(marknr as any, cvr);
        const mapsArray = Array.isArray(maps) ? maps : [];

        updatedFields.push({
          ...field,
          maps: {
            hasNDVI: mapsArray.some((m: any) =>
              (m as any).Type?.toLowerCase() === 'ndvi' || (m as any).MapType === 'NDVI'
            ),
            hasUkrudt: mapsArray.some((m: any) =>
              (m as any).Type?.toLowerCase() === 'weed' ||
              (m as any).Type?.toLowerCase() === 'ukrudt' ||
              (m as any).MapType === 'Ukrudt'
            ),
            totalMaps: mapsArray.length
          }
        });
      } catch (err) {
        console.error('loadMapsInfoFallback: Error for field', (field as any).Marknr, err);
        updatedFields.push({
          ...field,
          maps: { hasNDVI: false, hasUkrudt: false, totalMaps: 0 }
        });
      }
    }

    setFields(updatedFields);
  };

  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) return fields;

    const query = searchQuery.toLowerCase();
    return fields.filter((f: any) =>
      (f as any).Marknr?.toLowerCase().includes(query)
    );
  }, [fields, searchQuery]);

  // Sorter: CVR-marker foerst, derefter zoom-loaded
  // FIX: Brug numerisk sortering i stedet for alfabetisk
  const sortedFields = useMemo(() => {
    return [...filteredFields].sort((a: any, b: any) => {
      // Foerst: CVR-marker kommer foer zoom-loaded marker
      if ((a as any).isZoomLoaded && !(b as any).isZoomLoaded) return 1;
      if (!(a as any).isZoomLoaded && (b as any).isZoomLoaded) return -1;
      // Derefter: Numerisk sortering af Marknr
      return compareMarknr((a as any).Marknr || '', (b as any).Marknr || '');
    });
  }, [filteredFields]);

  const fieldCounts = useMemo(() => {
    const cvrCount = fields.filter((f: any) => !(f as any).isZoomLoaded).length;
    const zoomCount = fields.filter((f: any) => (f as any).isZoomLoaded).length;
    return { cvrCount, zoomCount, total: fields.length };
  }, [fields]);

  // FIX 2025-12-31: handleFieldClick modtager nu CVR-Marknr (fieldKey) for korrekt lookup
  const handleFieldClick = (fieldKey: string) => {
    // FIX: Find mark via unik CVR-Marknr key (ikke kun Marknr)
    const field = fields.find((f: any) => getUniqueFieldKey(f) === fieldKey);

    if (field) {
      setSelectedMarknr(field.Marknr);
      const navigationData = {
        marknr: (field as any).Marknr,
        cvr: (field as any).CVR,
        areal: (field as any).IMK_areal,
        id: (field as any).Id,
        geometry: (field as any).Geometry
      };
      onNavigateToField((field as any).Marknr, navigationData);
    } else {
      // Fallback: Hvis field ikke findes (burde ikke ske)
      console.warn('handleFieldClick: Field not found for key:', fieldKey);
      // Proev at parse marknr fra fieldKey (format: CVR-Marknr)
      const dashIndex = fieldKey.indexOf('-');
      if (dashIndex > 0) {
        const marknr = fieldKey.substring(dashIndex + 1);
        setSelectedMarknr(marknr);
        onNavigateToField(marknr);
      }
    }
  };

  const handleFieldHover = (marknr: string | null) => {
    setSelectedMarknr(marknr);
  };

  // Papirkurv handlers
  const handleTrashClick = () => {
    setTrashModalOpen(true);
  };

  const handleTrashClose = () => {
    setTrashModalOpen(false);
  };

  // Naar et kort gendannes, opdater taeeller og reload maps
  const handleMapRestored = useCallback(() => {
    // Reload deleted count
    loadDeletedCount();

    // Reload field maps counts
    if (fields.length > 0) {
      const plainFields = fields.map(f => ({
        Id: f.Id,
        Marknr: f.Marknr,
        CVR: f.CVR,
        IMK_areal: f.IMK_areal,
        Geometry: f.Geometry,
        CenterLat: f.CenterLat,
        CenterLon: f.CenterLon,
        isZoomLoaded: f.isZoomLoaded
      }));
      loadMapsInfo(plainFields);
    }
  }, [fields]);

  return (
    <div className="mine-marker-page">
      <aside className="mine-marker-sidebar">
        <button className="back-btn" onClick={onBack}>
          <span className="back-arrow">&larr;</span>
          Tilbage til dashboard
        </button>

        <div className="sidebar-title">
          <h2>Mine marker</h2>
          {!loading && fieldCounts.total > 0 && (
            <div className="field-counts">
              <span className="count-badge cvr-count" title="CVR marker">
                {fieldCounts.cvrCount}
              </span>
              {fieldCounts.zoomCount > 0 && (
                <span className="count-badge zoom-count" title="Tilføjede marker">
                  +{fieldCounts.zoomCount}
                </span>
              )}
            </div>
          )}
          {/* Papirkurv-ikon (erstatter info-btn) */}
          <button
            className="trash-btn"
            onClick={handleTrashClick}
            title="Papirkurv - slettede kort"
          >
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#333333" className="trash-icon"><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"/></svg>
            {deletedCount > 0 && (
              <span className="trash-badge">{deletedCount}</span>
            )}
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Henter dine marker...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button onClick={loadFields} className="retry-btn">
              Prøv igen
            </button>
          </div>
        ) : fields.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">&#127806;</div>
            <h3>Du har ingen marker endnu</h3>
            <p>Tilføj marker for at se dem her</p>
            <button className="add-first-btn" onClick={onNavigateToAddFields}>
              + Tilføj din første mark
            </button>
          </div>
        ) : (
          <>
            <MarkerListe
              fields={sortedFields as any}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onFieldClick={handleFieldClick}
              onFieldHover={handleFieldHover}
              selectedMarknr={selectedMarknr}
              loadingMaps={loadingMaps}
            />

            <div className="add-mark-container">
              <button className="add-mark-btn" onClick={onNavigateToAddFields}>
                + Tilføj mark
              </button>
            </div>
          </>
        )}
      </aside>

      <main className="mine-marker-map">
        <MarkerMap
          fields={sortedFields as any}
          onFieldClick={handleFieldClick}
          selectedMarknr={selectedMarknr}
          onFieldHover={handleFieldHover}
        />
      </main>

      {/* Papirkurv modal */}
      {trashModalOpen && (
        <TrashModal
          onClose={handleTrashClose}
          onMapRestored={handleMapRestored}
        />
      )}

      <style>{`
        .mine-marker-page {
          display: flex;
          height: calc(100vh - 56px);
          background: #f9fafb;
        }

        .mine-marker-sidebar {
          width: 380px;
          background: #f9fafb;
          border-right: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .back-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 16px 20px;
          background: white;
          border: none;
          border-bottom: 1px solid #e5e7eb;
          text-align: left;
          font-size: 14px;
          color: #374151;
          cursor: pointer;
          transition: background 0.15s;
        }

        .back-btn:hover {
          background: #f9fafb;
        }

        .back-arrow {
          font-size: 16px;
          color: #6b7280;
        }

        .sidebar-title {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 16px 20px;
          background: white;
          border-bottom: 1px solid #e5e7eb;
        }

        .sidebar-title h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #111827;
        }

        .field-counts {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-left: auto;
          margin-right: 8px;
        }

        .count-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 22px;
          padding: 0 6px;
          border-radius: 11px;
          font-size: 12px;
          font-weight: 600;
        }

        .cvr-count {
          background: #dcfce7;
          color: #16a34a;
        }

        .zoom-count {
          background: #dbeafe;
          color: #3b82f6;
        }

        /* Papirkurv knap */
        .trash-btn {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #f3f4f6;
          border: none;
          cursor: pointer;
          transition: all 0.15s;
        }

        .trash-btn:hover {
          background: #e5e7eb;
        }

        .trash-icon {
          font-size: 18px;
        }

        .trash-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 18px;
          height: 18px;
          padding: 0 4px;
          background: #dc2626;
          color: white;
          font-size: 11px;
          font-weight: 600;
          border-radius: 9px;
        }

        .loading-state,
        .error-state,
        .empty-state {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
          color: #6b7280;
        }

        .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #e5e7eb;
          border-top-color: #16a34a;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .retry-btn {
          margin-top: 12px;
          padding: 10px 20px;
          background: #3c6c34;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
        }

        .retry-btn:hover {
          background: #2A4C24;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          margin: 0 0 8px 0;
          font-size: 16px;
          color: #374151;
        }

        .empty-state p {
          margin: 0 0 20px 0;
          font-size: 14px;
        }

        .add-first-btn {
          padding: 12px 24px;
          background: #3c6c34;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }

        .add-first-btn:hover {
          background: #2A4C24;
        }

        .add-mark-container {
          padding: 16px 20px;
          background: #f9fafb;
        }

        .add-mark-btn {
          width: 100%;
          padding: 12px;
          background: #3c6c34;
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .add-mark-btn:hover {
          background: #2A4C24;
        }

        .mine-marker-map {
          flex: 1;
          position: relative;
          background: #e5e7eb;
        }
      `}</style>
    </div>
  );
};

export default MineMarkerOversigt;
